<!--
*  TTTech nerve-management-system-frontend
*  Copyright(c) 2026. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->

<template>
  <NerveDialog
    v-model="dialogState"
    scrollable
    persistent
    width="600"
    @keydown.esc="close()"
    @keydown.enter="sendApplyConfigurationRequest()"
  >
    <v-card>
      <v-card-title class="text-center">
        {{
          $t('nodes.deployedWorkloadControl.applyWorkloadConfigurationDialog.title', {
            workloadName: dialogParams.title,
          })
        }}
      </v-card-title>
      <v-form
        id="iiotApplyWorkloadConfigurationDialogForm"
        ref="iiotApplyWorkloadConfigurationDialogForm"
        v-model="valid"
        class="pa-0 my-8"
        enctype="multipart/form-data"
        @keyup.enter="sendApplyConfigurationRequest"
      >
        <div class="d-flex ml-3">
          <div class="flex-grow-1" style="max-width: 33%">
            <v-text-field
              id="iiotApplyWorkloadConfigurationDialogTheFileName"
              v-model="theFileName"
              :rules="theFileNameRule"
              disabled
              density="compact"
              :placeholder="$t('nodes.deployedWorkloadControl.fileNamePlaceholder')"
              class="default-input pa-0 ml-4 mt-0"
              required
            />
          </div>
          <div class="flex-grow-1 ml-7" style="max-width: 33%">
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <v-icon
                  id="iiotApplyWorkloadConfigurationDialogUploadBtn"
                  ref="iiotApplyWorkloadConfigurationDialogUploadBtn"
                  data-cy="iiotApplyWorkloadConfigurationDialogUploadBtn"
                  :class="{ hidden: showRemovedBtn }"
                  size="28"
                  color="primary"
                  class="mt-2"
                  v-bind="props"
                  @click="fileInputClick"
                >
                  mdi-plus
                </v-icon>
              </template>
              {{ $t('nodes.deployedWorkloadControl.applyWorkloadConfigurationDialog.uploadFileTooltip') }}
            </v-tooltip>
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <v-icon
                  id="iiotApplyWorkloadConfigurationDialogRemoveConfigurationFileBtn"
                  ref="iiotApplyWorkloadConfigurationDialogRemoveConfigurationFileBtn"
                  data-cy="iiotApplyWorkloadConfigurationDialogRemoveConfigurationFileBtn"
                  :class="{ hidden: !showRemovedBtn }"
                  size="28"
                  color="primary"
                  class="mt-2"
                  v-bind="props"
                  @click="removeConfigurationFileBtn"
                >
                  mdi-minus
                </v-icon>
              </template>
              {{ $t('nodes.deployedWorkloadControl.applyWorkloadConfigurationDialog.removeFileTooltip') }}
            </v-tooltip>
          </div>
          <input
            id="fileInput"
            ref="fileInput"
            type="file"
            style="display: none"
            accept=".zip"
            @change="fileChangeHandler"
            @click="$event.target.value = ''"
          />
        </div>
      </v-form>
      <v-card-actions>
        <NerveButton
          id="iiotApplyWorkloadConfigurationDialogCancelButton"
          ref="applyWorkloadConfigurationDialogCancelButton"
          :text="$t('nodes.deployedWorkloadControl.applyWorkloadConfigurationDialog.cancelButton')"
          type-of-btn="cancel"
          size="normal"
          autofocus
          @click-event="close"
        />
        <NerveButton
          id="iiotApplyWorkloadConfigurationDialogConfirmButton"
          ref="applyWorkloadConfigurationDialogConfirmButton"
          :disabled="!valid"
          :text="$t('nodes.deployedWorkloadControl.applyWorkloadConfigurationDialog.applyButton')"
          type-of-btn="action"
          size="normal"
          @click-event="sendApplyConfigurationRequest"
        />
      </v-card-actions>
    </v-card>
  </NerveDialog>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import { NerveButton, NerveDialog } from '@nerve/ui-components';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import useNodesStore from '@/store/modules/pinia/nodes';

// Stores
const utilsStore = useUtilsStore();
const nodesStore = useNodesStore();

const valid = ref(false);
const showRemovedBtn = ref(false);
const files = ref([]);
const theFileNameRule = [(v) => !!v || ''];
const fileInput = ref(null);

const dialogState = computed({
  get() {
    return utilsStore.applyWorkloadConfigurationDialog;
  },
  set() {
    close();
  },
});

const dialogParams = computed(() => {
  return utilsStore.applyWorkloadConfigurationDialogParams;
});

const theFileName = computed(() => {
  if (files.value.length > 0 && 'name' in files.value[0]) {
    return files.value[0].name;
  }
  return '';
});

function removeConfigurationFileBtn() {
  files.value = [];
  if (fileInput.value) fileInput.value.value = null;
  showRemovedBtn.value = false;
}

function fileChangeHandler(e) {
  files.value = e.target.files;
  showRemovedBtn.value = true;
}

function fileInputClick() {
  nextTick(() => {
    if (fileInput.value) fileInput.value.click();
  });
}

function close() {
  files.value = [];
  if (fileInput.value) fileInput.value.value = null;
  showRemovedBtn.value = false;
  utilsStore.closeApplyWorkloadConfiguration();
}

async function sendApplyConfigurationRequest() {
  const workload = nodesStore.selectedDeployedWorkload;
  const formData = new FormData();
  formData.append('file', files.value[0]);
  let formParams;
  if (nodesStore.isNewComposeSupported && workload.type === 'docker-compose') {
    const { configurationUpdateInfo } = dialogParams.value.service;
    formParams = {
      action: 'apply',
      service: configurationUpdateInfo.serviceName,
      volume: {
        name: configurationUpdateInfo.name,
        path: configurationUpdateInfo.path,
      },
      restartOnConfigurationUpdate: configurationUpdateInfo.restartOnConfigurationUpdate,
    };
  } else {
    formParams = {
      action: 'apply',
      volume: workload.configurationUpdateInfo.configurationVolumes[0],
      restartOnConfigurationUpdate: workload.configurationUpdateInfo.restartOnConfigurationUpdate,
    };
  }
  formParams.workloadId = workload.workloadId;
  formParams.versionId = workload.versionId;
  formParams.workloadName = workload.name;
  formParams.versionName = workload.workloadVersionName;
  formData.append('data', JSON.stringify(formParams));
  try {
    utilsStore.closeApplyWorkloadConfiguration();
    utilsStore.showLoadingBar();
    await nodesStore.applyWorkloadConfiguration(formData);
    utilsStore.showCustomToast({
      text: 'nodes.deployedWorkloadControl.applyWorkloadConfigurationDialog.successMessage',
      color: 'success',
      showClose: true,
    });
    files.value = Array.isArray(files.value) ? [] : files.value;
    showRemovedBtn.value = false;
  } catch {
    // No error handling here for request applied, interceptor handles errors
  } finally {
    files.value = [];
    utilsStore.closeLoadingBar();
  }
}
</script>
<style lang="scss">
#iiotApplyWorkloadConfigurationDialogRemoveConfigurationFileBtn.hidden,
#iiotApplyWorkloadConfigurationDialogUploadBtn.hidden {
  display: none;
}

#iiotApplyWorkloadConfigurationDialogTheFileName {
  opacity: 1 !important;
}
#iiotApplyWorkloadConfigurationDialogTheFileName::placeholder {
  opacity: 1 !important;
}
</style>
